import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Sparkles, Wand2, ImagePlus, Save, Loader2, Eye, RefreshCw, Check, X, Crop, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import ManualImageEditor from '@/components/ManualImageEditor';
import PrintQualityPanel from '@/components/PrintQualityPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ArtworkEditor = () => {
  const { artworkId } = useParams();
  const navigate = useNavigate();
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [enhancingText, setEnhancingText] = useState(false);
  const [enhancingImage, setEnhancingImage] = useState(false);
  const [autoCropping, setAutoCropping] = useState(false);
  const [newImageFile, setNewImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  // Before/After preview state
  const [imagePreview, setImagePreview] = useState(null); // { before, after, message, type }
  const [applyingImage, setApplyingImage] = useState(false);
  const [showManualEditor, setShowManualEditor] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', year: '', medium: '',
    dimensions: '', category: '', art_type: 'Traditional',
    featured: false, price: '', for_sale: true, tags: ''
  });

  const token = localStorage.getItem('admin_token');

  const fetchArtwork = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/artworks/${artworkId}`);
      const a = res.data;
      setArtwork(a);
      setForm({
        title: a.title || '',
        description: a.description || '',
        year: a.year || '',
        medium: a.medium || '',
        dimensions: a.dimensions || '',
        category: a.category || '',
        art_type: a.art_type || 'Traditional',
        featured: a.featured || false,
        price: a.price || '',
        for_sale: a.for_sale !== false,
        tags: (a.tags || []).join(', ')
      });
      setPreviewUrl(a.image_url);
    } catch {
      toast.error('Failed to load artwork');
      navigate('/admin/dashboard');
    } finally {
      setLoading(false);
    }
  }, [artworkId, navigate]);

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    fetchArtwork();
  }, [token, navigate, fetchArtwork]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    const fd = new FormData();
    Object.entries(form).forEach(([key, val]) => {
      if (key === 'tags') {
        const arr = val ? val.split(',').map(t => t.trim().replace(/^#/, '')).filter(Boolean) : [];
        fd.append('tags', JSON.stringify(arr));
      } else if (val !== null && val !== '') {
        fd.append(key, val);
      }
    });
    if (newImageFile) fd.append('image', newImageFile);
    try {
      await axios.put(`${API}/artworks/${artworkId}`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Artwork saved');
      await fetchArtwork();
      setNewImageFile(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleAiAnalyze = async () => {
    setAnalyzing(true);
    try {
      let res;
      if (newImageFile) {
        const fd = new FormData();
        fd.append('image', newImageFile);
        res = await axios.post(`${API}/artworks/analyze-image`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
          timeout: 60000
        });
      } else {
        res = await axios.post(`${API}/artworks/${artworkId}/analyze`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000
        });
      }
      if (res.data.success) {
        setForm(prev => ({
          ...prev,
          title: res.data.title || prev.title,
          description: res.data.description || prev.description,
          medium: res.data.medium || prev.medium,
          category: res.data.category || prev.category,
          tags: (res.data.tags || []).join(', ') || prev.tags
        }));
        toast.success('AI analysis complete — fields updated');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleEnhanceText = async () => {
    setEnhancingText(true);
    try {
      const tagsArr = form.tags ? form.tags.split(',').map(t => t.trim().replace(/^#/, '')).filter(Boolean) : [];
      const res = await axios.post(`${API}/artworks/${artworkId}/enhance-text`, {
        title: form.title,
        description: form.description,
        tags: tagsArr
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      });
      if (res.data.success) {
        setForm(prev => ({
          ...prev,
          title: res.data.title || prev.title,
          description: res.data.description || prev.description,
          tags: (res.data.tags || []).join(', ') || prev.tags
        }));
        toast.success('Text enhanced — review the changes');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Text enhancement failed');
    } finally {
      setEnhancingText(false);
    }
  };

  const handleEnhanceImage = async () => {
    setEnhancingImage(true);
    try {
      const res = await axios.post(`${API}/artworks/${artworkId}/enhance-image`, { preview: true }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000
      });
      if (res.data.success && res.data.preview_image) {
        setImagePreview({
          before: previewUrl,
          after: res.data.preview_image,
          message: res.data.message,
          type: 'enhance',
          sizes: `${res.data.original_size} → ${res.data.new_size}`,
        });
      } else {
        toast.info(res.data.message || 'No changes made');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image enhancement failed');
    } finally {
      setEnhancingImage(false);
    }
  };

  const handleAutoCrop = async () => {
    setAutoCropping(true);
    try {
      const res = await axios.post(`${API}/artworks/${artworkId}/auto-crop`, { preview: true }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000
      });
      if (res.data.success && res.data.preview_image) {
        setImagePreview({
          before: previewUrl,
          after: res.data.preview_image,
          message: res.data.message,
          type: 'crop',
          sizes: `${res.data.original_size} → ${res.data.new_size}`,
        });
      } else {
        toast.info(res.data.message || 'No crop needed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-crop failed');
    } finally {
      setAutoCropping(false);
    }
  };

  const handleAcceptPreview = async () => {
    if (!imagePreview?.after) return;
    setApplyingImage(true);
    try {
      await axios.post(`${API}/artworks/${artworkId}/apply-image`, {
        image_url: imagePreview.after
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000
      });
      toast.success('Changes applied');
      setImagePreview(null);
      await fetchArtwork();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply changes');
    } finally {
      setApplyingImage(false);
    }
  };

  const handleRejectPreview = () => {
    setImagePreview(null);
    toast.info('Changes discarded');
  };

  const handleManualEditApply = async (dataUrl) => {
    try {
      await axios.post(`${API}/artworks/${artworkId}/apply-image`, {
        image_url: dataUrl
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000
      });
      toast.success('Manual edits applied successfully');
      setShowManualEditor(false);
      await fetchArtwork();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply edits');
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-24 flex items-center justify-center">
        <Loader2 className="animate-spin text-accent" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="artwork-editor-page">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-accent mb-2 transition-colors"
              data-testid="back-to-dashboard-link"
            >
              <ArrowLeft size={16} /> Back to Dashboard
            </button>
            <h1 className="font-serif text-3xl sm:text-4xl font-light tracking-tight" data-testid="editor-title">
              Edit Artwork
            </h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => window.open(`/gallery`, '_blank')} data-testid="preview-gallery-btn">
              <Eye size={14} className="mr-1.5" /> Preview Gallery
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-accent hover:bg-accent/90" data-testid="save-artwork-btn">
              {saving ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Saving...</> : <><Save size={14} className="mr-1.5" /> Save Changes</>}
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left: Image + AI Image Tools */}
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="image-preview-card">
              {showManualEditor ? (
                <div className="p-4">
                  <ManualImageEditor
                    imageUrl={previewUrl}
                    onApply={handleManualEditApply}
                    onCancel={() => setShowManualEditor(false)}
                  />
                </div>
              ) : imagePreview ? (
                /* Before/After Comparison */
                <div data-testid="before-after-preview">
                  <div className="px-4 pt-4 pb-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-sans uppercase tracking-[0.12em] text-amber-600 font-medium">
                        {imagePreview.type === 'crop' ? 'Auto-Crop Preview' : 'Enhancement Preview'}
                      </span>
                      <span className="text-[10px] font-sans text-muted-foreground">{imagePreview.sizes}</span>
                    </div>
                    <p className="text-xs text-muted-foreground font-sans mb-3">{imagePreview.message}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-px bg-border">
                    <div className="bg-muted/20 p-2">
                      <p className="text-[10px] font-sans uppercase tracking-[0.12em] text-muted-foreground text-center mb-1.5">Before</p>
                      <div className="aspect-square flex items-center justify-center">
                        <img src={imagePreview.before} alt="Before" className="max-w-full max-h-full object-contain rounded" data-testid="before-image" />
                      </div>
                    </div>
                    <div className="bg-muted/20 p-2">
                      <p className="text-[10px] font-sans uppercase tracking-[0.12em] text-accent text-center mb-1.5">After</p>
                      <div className="aspect-square flex items-center justify-center">
                        <img src={imagePreview.after} alt="After" className="max-w-full max-h-full object-contain rounded" data-testid="after-image" />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 p-4">
                    <Button
                      onClick={handleAcceptPreview}
                      disabled={applyingImage}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      data-testid="accept-preview-btn"
                    >
                      {applyingImage ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Applying...</> : <><Check size={14} className="mr-1.5" /> Accept</>}
                    </Button>
                    <Button
                      onClick={handleRejectPreview}
                      variant="outline"
                      className="flex-1 border-red-200 text-red-700 hover:bg-red-50"
                      data-testid="reject-preview-btn"
                    >
                      <X size={14} className="mr-1.5" /> Reject
                    </Button>
                  </div>
                </div>
              ) : (
                /* Normal Image Preview */
                <>
                  <div className="aspect-square flex items-center justify-center bg-muted/20 p-4">
                    {previewUrl ? (
                      <img src={previewUrl} alt={form.title} className="max-w-full max-h-full object-contain rounded" data-testid="artwork-preview-image" />
                    ) : (
                      <div className="text-muted-foreground text-sm">No image</div>
                    )}
                  </div>
                  <div className="p-4 space-y-3">
                    <div>
                      <Label htmlFor="editor-image">Replace Image</Label>
                      <Input
                        id="editor-image"
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="mt-1"
                        data-testid="editor-image-input"
                      />
                    </div>
                    {newImageFile && (
                      <p className="text-xs text-amber-600 font-sans">New image selected — click Save to apply</p>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Tools Panel */}
            <div className="bg-card border border-border rounded-lg p-5 space-y-4" data-testid="ai-tools-panel">
              <h3 className="font-sans text-xs uppercase tracking-[0.15em] text-muted-foreground">Image & AI Tools</h3>

              <Button
                onClick={() => setShowManualEditor(true)}
                disabled={!previewUrl || showManualEditor}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white justify-start"
                data-testid="manual-edit-btn"
              >
                <Pencil size={14} className="mr-2" /> Manual Crop & Adjust
              </Button>
              <p className="text-[11px] text-muted-foreground -mt-2 ml-1">
                Manually crop, rotate, and adjust brightness/contrast/saturation
              </p>

              <hr className="border-border" />

              <Button
                onClick={handleAiAnalyze}
                disabled={analyzing}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white justify-start"
                data-testid="ai-analyze-btn"
              >
                {analyzing ? <><Loader2 size={14} className="mr-2 animate-spin" /> Analyzing...</> : <><Sparkles size={14} className="mr-2" /> AI Analyze (Auto-fill All Fields)</>}
              </Button>
              <p className="text-[11px] text-muted-foreground -mt-2 ml-1">
                Generates title, description, medium, category, and tags from the image
              </p>

              <Button
                onClick={handleEnhanceText}
                disabled={enhancingText || !form.title}
                variant="outline"
                className="w-full border-violet-200 text-violet-700 hover:bg-violet-50 justify-start"
                data-testid="ai-enhance-text-btn"
              >
                {enhancingText ? <><Loader2 size={14} className="mr-2 animate-spin" /> Enhancing...</> : <><Wand2 size={14} className="mr-2" /> AI Enhance Text</>}
              </Button>
              <p className="text-[11px] text-muted-foreground -mt-2 ml-1">
                Improves existing title, description, and tags for SEO and gallery appeal
              </p>

              <Button
                onClick={handleAutoCrop}
                disabled={autoCropping}
                variant="outline"
                className="w-full border-sky-200 text-sky-700 hover:bg-sky-50 justify-start"
                data-testid="ai-auto-crop-btn"
              >
                {autoCropping ? <><Loader2 size={14} className="mr-2 animate-spin" /> Cropping...</> : <><ImagePlus size={14} className="mr-2" /> Auto-Crop & Straighten</>}
              </Button>
              <p className="text-[11px] text-muted-foreground -mt-2 ml-1">
                Detects painting edges, removes background, and corrects perspective
              </p>

              <Button
                onClick={handleEnhanceImage}
                disabled={enhancingImage}
                variant="outline"
                className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50 justify-start"
                data-testid="ai-enhance-image-btn"
              >
                {enhancingImage ? <><Loader2 size={14} className="mr-2 animate-spin" /> Enhancing Image...</> : <><RefreshCw size={14} className="mr-2" /> Enhance Quality</>}
              </Button>
              <p className="text-[11px] text-muted-foreground -mt-2 ml-1">
                Auto-improves sharpness, contrast, color balance, and brightness
              </p>
            </div>

            {/* Print quality + optional high-res print master */}
            {artwork && <PrintQualityPanel artwork={artwork} token={token} onChanged={fetchArtwork} />}

            {/* Artwork Metadata */}
            {artwork && (
              <div className="bg-card border border-border rounded-lg p-5 space-y-2" data-testid="artwork-metadata">
                <h3 className="font-sans text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">Metadata</h3>
                <p className="text-xs text-muted-foreground">ID: <span className="font-mono text-foreground">{artwork.id}</span></p>
                {artwork.image_width && (
                  <p className="text-xs text-muted-foreground">Dimensions: {artwork.image_width} x {artwork.image_height}px</p>
                )}
                <p className="text-xs text-muted-foreground">Tags: {(artwork.tags || []).length}</p>
              </div>
            )}
          </div>

          {/* Right: Form Fields */}
          <div className="lg:col-span-3 space-y-5">
            <div className="bg-card border border-border rounded-lg p-6 space-y-5" data-testid="artwork-form">
              <div>
                <Label htmlFor="ed-title">Title *</Label>
                <Input id="ed-title" value={form.title} onChange={(e) => handleChange('title', e.target.value)} data-testid="ed-title-input" />
              </div>

              <div>
                <Label htmlFor="ed-description">Description</Label>
                <Textarea id="ed-description" value={form.description} onChange={(e) => handleChange('description', e.target.value)} rows={5} data-testid="ed-description-input" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ed-year">Year</Label>
                  <Input id="ed-year" value={form.year} onChange={(e) => handleChange('year', e.target.value)} data-testid="ed-year-input" />
                </div>
                <div>
                  <Label htmlFor="ed-category">Category</Label>
                  <Input id="ed-category" value={form.category} onChange={(e) => handleChange('category', e.target.value)} data-testid="ed-category-input" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ed-medium">Medium</Label>
                  <Input id="ed-medium" value={form.medium} onChange={(e) => handleChange('medium', e.target.value)} data-testid="ed-medium-input" />
                </div>
                <div>
                  <Label htmlFor="ed-dimensions">Dimensions</Label>
                  <Input id="ed-dimensions" value={form.dimensions} onChange={(e) => handleChange('dimensions', e.target.value)} placeholder="e.g., 8ft x 5ft" data-testid="ed-dimensions-input" />
                </div>
              </div>

              <div>
                <Label htmlFor="ed-tags">Tags / Hashtags</Label>
                <Textarea
                  id="ed-tags"
                  value={form.tags}
                  onChange={(e) => handleChange('tags', e.target.value)}
                  placeholder="krishna, devotional, oil-painting, blue, spiritual"
                  rows={2}
                  data-testid="ed-tags-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Comma-separated. Use AI Analyze or AI Enhance to auto-generate.</p>
              </div>

              <div>
                <Label htmlFor="ed-art-type">Art Type</Label>
                <select
                  id="ed-art-type"
                  value={form.art_type}
                  onChange={(e) => handleChange('art_type', e.target.value)}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg font-sans text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  data-testid="ed-art-type-select"
                >
                  <option value="Traditional">Traditional (Oil, Canvas, Watercolor, etc.)</option>
                  <option value="Digital">Digital Art</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ed-price">Price (INR)</Label>
                  <Input
                    id="ed-price"
                    type="number"
                    min="0"
                    step="100"
                    value={form.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    placeholder={form.art_type === 'Digital' ? '5000' : '10000'}
                    data-testid="ed-price-input"
                  />
                </div>
                <div className="flex items-end pb-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="ed-for-sale"
                      checked={form.for_sale}
                      onCheckedChange={(checked) => handleChange('for_sale', checked)}
                      data-testid="ed-for-sale-checkbox"
                    />
                    <Label htmlFor="ed-for-sale" className="cursor-pointer">Available for sale</Label>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ed-featured"
                  checked={form.featured}
                  onCheckedChange={(checked) => handleChange('featured', checked)}
                  data-testid="ed-featured-checkbox"
                />
                <Label htmlFor="ed-featured" className="cursor-pointer">Featured artwork (homepage)</Label>
              </div>
            </div>

            {/* Save bar */}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => navigate('/admin/dashboard')} data-testid="ed-cancel-btn">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-accent hover:bg-accent/90 min-w-[140px]" data-testid="ed-save-btn">
                {saving ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Saving...</> : <><Save size={14} className="mr-1.5" /> Save Changes</>}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtworkEditor;
